import { useMemo, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
	Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, useToast
} from '@hiring-intelligence/ui';
import { Download, Pencil, Save, X } from 'lucide-react';
import { cvGenerationApi } from '@/services/api';

interface CVPreviewModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	content: string;
	title: string;
	cvId?: string;
	onContentSaved?: (newContent: string) => void;
}

/** Strip wrapping code fences (```markdown … ``` or ``` … ```) so the markdown renders properly */
function stripCodeFences(raw: string): string {
	const trimmed = raw.trim();
	const match = trimmed.match(/^```(?:\w+)?\s*\n([\s\S]*?)\n\s*```$/);
	return match ? match[1] : trimmed;
}

/** Insert GFM header-separator rows into pipe tables that are missing them so react-markdown renders them as HTML tables */
function fixMarkdownTables(md: string): string {
	return md.replace(
		/((?:^|\n)\|[^\n]+\|(?:\n\|[^\n]+\|)+)/g,
		(tableBlock) => {
			const lines = tableBlock.split('\n').filter(l => l.trim());
			if (lines.length >= 2 && /^\|[\s-:|]+\|$/.test(lines[1].trim())) return tableBlock;
			const cols = lines[0].split('|').length - 2;
			const sep = '|' + ' --- |'.repeat(cols);
			return [lines[0], sep, ...lines.slice(1)].join('\n');
		}
	);
}

export function CVPreviewModal({ open, onOpenChange, content, title, cvId, onContentSaved }: CVPreviewModalProps) {
	const cleanContent = useMemo(() => fixMarkdownTables(stripCodeFences(content)), [content]);
	const [isEditing, setIsEditing] = useState(false);
	const [editContent, setEditContent] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const { toast } = useToast();

	const handleEdit = useCallback(() => {
		setEditContent(cleanContent);
		setIsEditing(true);
	}, [cleanContent]);

	const handleCancel = useCallback(() => {
		setIsEditing(false);
		setEditContent('');
	}, []);

	const handleSave = useCallback(async () => {
		if (!cvId) return;
		setIsSaving(true);
		try {
			await cvGenerationApi.updateContent(cvId, editContent);
			toast({ title: 'Saved', description: 'CV content updated successfully.' });
			setIsEditing(false);
			onContentSaved?.(editContent);
		} catch {
			toast({ title: 'Error', description: 'Failed to save changes.', variant: 'destructive' });
		} finally {
			setIsSaving(false);
		}
	}, [cvId, editContent, toast, onContentSaved]);

	const handleDownloadPDF = async () => {
		if (!cvId) return;
		const { blob, filename } = await cvGenerationApi.downloadPDF(cvId);
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename || `${title.replace(/\s+/g, '-').toLowerCase()}.pdf`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const displayContent = isEditing ? editContent : cleanContent;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<div className="flex items-center justify-between">
						<div>
							<DialogTitle>{title}</DialogTitle>
							<DialogDescription>
								{isEditing ? 'Editing CV content (Markdown)' : 'Generated CV preview'}
							</DialogDescription>
						</div>
						<div className="flex items-center gap-2">
							{isEditing ? (
								<>
									<Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
										<X className="h-3 w-3 mr-1" /> Cancel
									</Button>
									<Button size="sm" onClick={handleSave} disabled={isSaving}>
										<Save className="h-3 w-3 mr-1" /> {isSaving ? 'Saving...' : 'Save'}
									</Button>
								</>
							) : (
								<>
									{cvId && (
										<Button variant="outline" size="sm" onClick={handleEdit}>
											<Pencil className="h-3 w-3 mr-1" /> Edit
										</Button>
									)}
									{cvId && (
										<Button variant="outline" size="sm" onClick={handleDownloadPDF}>
											<Download className="h-3 w-3 mr-1" /> Download PDF
										</Button>
									)}
								</>
							)}
						</div>
					</div>
				</DialogHeader>

				{isEditing ? (
					<div className="grid grid-cols-2 gap-4">
						{/* Editor */}
						<div className="flex flex-col">
							<div className="text-xs font-medium text-muted-foreground mb-1">Markdown Editor</div>
							<textarea
								className="w-full h-[65vh] p-4 font-mono text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary bg-muted/30"
								value={editContent}
								onChange={(e) => setEditContent(e.target.value)}
								spellCheck={false}
							/>
						</div>
						{/* Live preview */}
						<div className="flex flex-col">
							<div className="text-xs font-medium text-muted-foreground mb-1">Preview</div>
							<div className="h-[65vh] overflow-y-auto rounded-md bg-white border p-6 text-black">
								<div className="prose prose-sm max-w-none prose-table:border-collapse prose-th:border-none prose-td:border-none prose-th:py-0.5 prose-td:py-0.5 prose-th:pr-2 prose-th:font-semibold prose-h2:text-base prose-h2:font-bold prose-h2:mt-4 prose-h2:mb-2 prose-h2:border-0 prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-3 prose-h3:mb-1 prose-p:text-sm prose-li:text-sm prose-li:my-0.5">
									<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
										{editContent}
									</ReactMarkdown>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="rounded-md bg-white p-6 text-black">
						<div className="prose prose-sm max-w-none prose-table:border-collapse prose-th:border-none prose-td:border-none prose-th:py-0.5 prose-td:py-0.5 prose-th:pr-2 prose-th:font-semibold prose-h2:text-base prose-h2:font-bold prose-h2:mt-4 prose-h2:mb-2 prose-h2:border-0 prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-3 prose-h3:mb-1 prose-p:text-sm prose-li:text-sm prose-li:my-0.5">
							<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
								{displayContent}
							</ReactMarkdown>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
